import React, { useCallback, useMemo } from "react"
import { CellContext } from "@tanstack/react-table"

import {
  formatValue,
  safeFormat,
} from "~/ui-rtk/components/ui/charts/TableVisualization/utils"
import type {
  TMetric,
  TSources,
  TableVisualizationColumn,
} from "~/ui-rtk/constants/table-visualization-types"
import { URLValidator } from "~/ui-rtk/utils/validators"
import { renderImage, renderSourceIcon } from "./utils"
import { ImageSvg } from "~/ui-rtk/components/ui/svg/essentials"
import ValueWithDelta from "~/ui-rtk/components/ui/common/ValueWithDelta/ValueWithDelta"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { CaptionPreviewNumber } from "~/ui-rtk/components/ui/common/CaptionPreviewNumber"
import {
  ConvertNumberTypeArr,
  type ConvertNumberType,
} from "~/ui-rtk/utils/number-utils"
import { SelectionCheckbox } from "../SelectionCheckbox"
import DropdownMenu, {
  type TDropdownMenuOption,
} from "~/ui-rtk/components/ui/controls/DropdownMenu/DropdownMenu"
import { ChevronDownIcon } from "@heroicons/react/24/outline"

type TCellProps = {
  sources?: TSources
  column: TableVisualizationColumn & {
    cellType?: "dropdown" | "custom-component" | "combined-range"
    ranges?: string[]
    getDropdownOptions?: (
      rowData: TMetric,
    ) => Array<{ value: unknown; label: string }>
    onDropdownChange?: (value: unknown, rowData: TMetric) => void
    getCurrentValue?: (rowData: TMetric) => string
    customComponent?: (props: {
      rowData: TMetric
      cellValue: unknown
      onStopPropagation: (e: React.MouseEvent) => void
    }) => React.ReactNode
  }
  cellContext: CellContext<TMetric, unknown>
  compareWith?: {
    key: string | undefined
    data: TMetric[] | null
  }
}

export const Cell: React.FC<TCellProps> = ({
  sources,
  column,
  cellContext,
  compareWith,
}) => {
  const cellValue = cellContext.getValue()
  const rowData = cellContext.row.original

  // Get raw numeric value from row data for comparison calculations
  const rawCellValue = rowData[column.key]

  const compareRow = useMemo(() => {
    const compareUniqueKey = compareWith?.key
    if (!compareUniqueKey) {
      return null
    }

    // Use row.original[compareUniqueKey] so we match on raw data (e.g. "Mobile").
    // getValue(compareUniqueKey) can return JSX when that column has custom render.
    const cellRowValue = rowData[compareUniqueKey]
    const norm = (v: unknown) =>
      String(v ?? "")
        .toLowerCase()
        .trim()
    return compareWith.data?.find(
      row => norm(row[compareUniqueKey]) === norm(cellRowValue),
    )
  }, [compareWith?.key, compareWith?.data, rowData])

  const isLastRow = useMemo(() => {
    const rowIndex = cellContext.row.index
    const hasNextRow =
      !!cellContext.table.getSortedRowModel().rows[rowIndex + 1]
    return !hasNextRow
  }, [cellContext])

  const renderCell = useCallback(() => {
    // Handle custom component cell type
    if (column.cellType === "custom-component" && column.customComponent) {
      return column.customComponent({
        rowData,
        cellValue,
        onStopPropagation: (e: React.MouseEvent) => e.stopPropagation(),
      })
    }

    // Handle dropdown cell type
    if (column.cellType === "dropdown" && column.getDropdownOptions) {
      const dropdownOptions = column.getDropdownOptions(
        rowData,
      ) as TDropdownMenuOption[]
      // For dropdown cells, prioritize getCurrentValue function over cellValue
      // This is especially important for custom dropdowns that manage their own state
      const currentValue = column.getCurrentValue
        ? column.getCurrentValue(rowData)
        : cellValue || ""

      return (
        <div onClick={e => e.stopPropagation()}>
          <DropdownMenu
            options={dropdownOptions}
            onItemClick={value => {
              column.onDropdownChange?.(value, rowData)
            }}
            className="btn-sm h-8 min-h-8 px-2 py-1 text-sm border border-gray-300 hover:border-gray-400 bg-white/5 hover:bg-white/10 text-white justify-between w-full"
          >
            <span className="text-left flex-1 truncate">
              {currentValue
                ? dropdownOptions.find(opt => opt.value === currentValue)
                    ?.label || "Select..."
                : "Select..."}
            </span>
            <ChevronDownIcon className="w-4 h-4 ml-2 flex-shrink-0" />
          </DropdownMenu>
        </div>
      )
    }

    // Original cell logic
    if (column.key === "select") {
      if (cellContext.row) {
        return (
          <SelectionCheckbox
            checked={cellContext.row.getIsSelected()}
            indeterminate={cellContext.row.getIsSomeSelected()}
            onChange={cellContext.row.getToggleSelectedHandler()}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          />
        )
      }
      // Header cell
      return (
        <SelectionCheckbox
          checked={cellContext.table.getIsAllRowsSelected()}
          indeterminate={cellContext.table.getIsSomeRowsSelected()}
          onChange={cellContext.table.getToggleAllRowsSelectedHandler()}
        />
      )
    }

    if (typeof cellValue === "object" && cellValue !== null) {
      return cellValue
    }

    if (column.type !== "image") {
      if (ConvertNumberTypeArr.includes(column.type as ConvertNumberType)) {
        return (
          <CaptionPreviewNumber
            value={cellValue as number}
            top={isLastRow}
            columnType={column.type as ConvertNumberType}
          />
        )
      }
      return safeFormat(
        cellValue as string | number | null | undefined,
        column.type,
        sources,
      )
    }

    if (sources?.has((cellValue as string)?.toUpperCase())) {
      const sourceKey = (cellValue as string).toUpperCase()
      const source = sources.get(sourceKey)
      if (source) {
        return renderSourceIcon(source)
      }
      return "--"
    }

    if (cellValue && URLValidator.validate(cellValue as string)) {
      return renderImage(cellValue as string, "Post Preview")
    }

    return <ImageSvg size={42} fill="basic.grey.inactive" />
  }, [cellValue, column, sources, cellContext, rowData])

  const cellClasses = useMemo(
    () => cn([column.classes?.cell, "text-md text-ellipsis"]),
    [column],
  )

  return (
    <div className={cellClasses}>
      {renderCell() as React.ReactNode}
      {compareWith &&
      ["currency", "number", "percent", "coef"].includes(`${column.type}`) ? (
        <span className="text-gray-light">
          {compareRow ? (
            <div className="flex gap-2 text-sm">
              <ValueWithDelta
                actualValue={Number(rawCellValue ?? 0)}
                comparedValue={Number(compareRow?.[column.key] ?? 0)}
                hideActualValue
              />
              <span>
                prev:{" "}
                {column.type === "percent" ||
                column.key.toLowerCase().includes("conversion") ||
                column.key.toLowerCase().includes("abandon")
                  ? `${Number(compareRow?.[column.key] ?? 0).toFixed(2)}%`
                  : formatValue(
                      compareRow?.[column.key] as string | number,
                      column.type,
                      undefined,
                    )}
              </span>
            </div>
          ) : (
            " (N/A)"
          )}
        </span>
      ) : null}
    </div>
  )
}
